import { Fn }		from "ts-base/fn";
import { Compared }	from "ts-base/compared";
import { Monoid }	from "ts-base/monoid";

export type Comparator<T> = (a:T, b:T) => Compared;

export namespace Comparator {
	export const builtin	= <T>(a:T, b:T):Compared =>
		a > b ? +1 : a < b ? -1 : 0;

	export const builtinOf	= <T>():Comparator<T>	=> builtin;

	export const builtinBy	= <S, T>(func:Fn<S, T>):Comparator<S> =>
		contraMap(builtin)(func);

	export const contraMap	= <T>(base:Comparator<T>) => <S>(func:Fn<S, T>):Comparator<S> =>
		(a:S, b:S) => base(func(a), func(b));

	export const trivial	= <T>():Comparator<T>	=> (_a, _b) => 0;

	export const concat	= <T>(high:Comparator<T>, low:Comparator<T>):Comparator<T>	=>
		// eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
		(a, b) => high(a, b) || low(a, b);

	export const join	= <T>(highToLow:ReadonlyArray<Comparator<T>>):Comparator<T>	=>
		// equivalent, but only about half as fast
		// highToLow.reduce(concat, trivial());
		(a, b) => {
			for (const comparator of highToLow) {
				const current	= comparator(a, b);
				if (current !== 0)	return current;
			}
			return 0;
		};

	export const joinN	= <T>(...highToLow:ReadonlyArray<Comparator<T>>):Comparator<T>	=>
		join(highToLow);

	export const reverse	= <T>(self:Comparator<T>):Comparator<T>	=>
		(a, b) => self(b, a);

	//-----------------------------------------------------------------------------

	export const maximum	= <T>(comparator:Comparator<T>) => (a:T, b:T):T=>
		comparator(a, b) > 0 ? a : b;

	export const minimum	= <T>(comparator:Comparator<T>) => (a:T, b:T):T=>
		comparator(a, b) < 0 ? a : b;

	//-----------------------------------------------------------------------------
	//## typeclassses

	export const monoid	= <T>():Monoid<Comparator<T>>	=> ({
		empty:	trivial(),
		concat:	concat,
	});
}
